import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { NTA_PALETTE_ICON } from '../Instructions/ntaInstructionsContent';
import { NTA_PALETTE_LIGHT_TEXT_ICONS } from '../ntaPaletteStatus';
import { NTA_COLORS } from '../constants';
import { ntaPageCss } from '../typography';

const ICON_BASE = '/nta-experience/instructions';

const ICON_SRC = {
  [NTA_PALETTE_ICON.NOT_ANSWERED]: `${ICON_BASE}/not-answered.svg`,
  [NTA_PALETTE_ICON.ANSWERED]: `${ICON_BASE}/answered.svg`,
  [NTA_PALETTE_ICON.MARKED_FOR_REVIEW]: `${ICON_BASE}/marked-for-review.svg`,
  [NTA_PALETTE_ICON.ANSWERED_MARKED]: `${ICON_BASE}/answered-marked.svg`
};

const ALT_TEXT = {
  [NTA_PALETTE_ICON.NOT_ANSWERED]: 'Not answered',
  [NTA_PALETTE_ICON.ANSWERED]: 'Answered',
  [NTA_PALETTE_ICON.MARKED_FOR_REVIEW]: 'Marked for review',
  [NTA_PALETTE_ICON.ANSWERED_MARKED]: 'Answered and marked for review',
  [NTA_PALETTE_ICON.NOT_VISITED]: 'Not visited'
};

const Wrap = styled.span`
  position: relative;
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: block;
  ${ntaPageCss}
`;

const Label = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: ${({ $lineHeight }) => $lineHeight}px;
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${({ $light }) =>
    $light ? 'var(--color-i-white)' : 'var(--color-text-secondary)'};
  pointer-events: none;
`;

const NotVisitedShape = styled.span`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  border: ${({ $borderWidth }) => $borderWidth}px solid
    ${NTA_COLORS.paletteNotVisitedBorder};
  border-radius: ${({ $radius }) => $radius}px;
  background: linear-gradient(
    180deg,
    ${NTA_COLORS.paletteNotVisitedStart} 0%,
    ${NTA_COLORS.paletteNotVisitedEnd} 100%
  );
  box-sizing: border-box;
`;

const LegendImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const formatLabel = (label) => {
  if (label === undefined || label === null) {
    return null;
  }
  if (typeof label === 'number') {
    return String(label);
  }
  return String(label).padStart(2, '0');
};

const NtaPaletteStatusVisual = ({ icon, label, size = 32 }) => {
  const displayLabel = formatLabel(label);
  const isLightText = NTA_PALETTE_LIGHT_TEXT_ICONS.has(icon);
  const fontSize = size >= 38 ? 14 : 12;
  const lineHeight = 16;
  const fontWeight = 400;
  const borderWidth = 2;
  const radius = 3;

  if (icon === NTA_PALETTE_ICON.NOT_VISITED) {
    return (
      <Wrap $size={size} aria-hidden={!displayLabel}>
        <NotVisitedShape $borderWidth={borderWidth} $radius={radius} />
        {displayLabel ? (
          <Label
            $fontSize={fontSize}
            $lineHeight={lineHeight}
            $fontWeight={fontWeight}
          >
            {displayLabel}
          </Label>
        ) : null}
      </Wrap>
    );
  }

  const src = ICON_SRC[icon];
  if (!src) {
    return null;
  }

  return (
    <Wrap $size={size}>
      <LegendImg
        src={src}
        width={size}
        height={size}
        alt={ALT_TEXT[icon] ?? ''}
      />
      {displayLabel ? (
        <Label
          $light={isLightText}
          $fontSize={fontSize}
          $lineHeight={lineHeight}
          $fontWeight={fontWeight}
        >
          {displayLabel}
        </Label>
      ) : null}
    </Wrap>
  );
};

export default NtaPaletteStatusVisual;

NtaPaletteStatusVisual.propTypes = {
  icon: PropTypes.oneOf(Object.values(NTA_PALETTE_ICON)).isRequired,
  label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.number
};
